/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A RELEASE DO HOST OFFLINE — um lugar só, porque quatro telas a citam
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A página de download, o cartão da landing e os dois modais da sala offline
 * falam do mesmo arquivo. Espalhar versão e link por quatro arquivos é como as
 * duas paletas do host nasceram: cada lugar decidindo o mesmo fato em separado.
 *
 * ─── POR QUE É CONSTANTE, E NÃO CONSULTA À API ─────────────────────────────
 *
 * A tentação é perguntar ao GitHub qual é a última versão. Não dá, e a razão é
 * medida: o `Portal-Educacional` é PRIVADO, e release de repositório privado é
 * privada — a URL responde 404 para quem não está logado com acesso. Por isso o
 * binário passa a ser publicado num repositório PÚBLICO só de releases, e por
 * isso a versão vem daqui, escrita.
 *
 * ─── CONSTANTE ENVELHECE, E É POR ISSO QUE O CI CONFERE ────────────────────
 *
 * Um número escrito à mão fica velho no dia seguinte. A defesa não é lembrar: é
 * o workflow do instalador comparar a tag com este arquivo e DERRUBAR o build
 * quando divergirem — a mesma guarda que já existe contra publicar `1.0.0`.
 */

/** O repositório público que hospeda só o binário. O portal continua privado. */
const REPO_PUBLICO = 'https://github.com/vengefful/host-sala-offline';

/**
 * ─── ONDE O ARQUIVO É SERVIDO ──────────────────────────────────────────────
 *
 * `true` faz o botão apontar para o próprio domínio, com o GitHub citado como
 * espelho. Três razões para preferir o portal quando ele existir:
 *
 *  1. o domínio bate com o site — baixar um `.exe` de portalsimulados.com.br é
 *     outra coisa que baixar de um github.com que a pessoa não conhece;
 *  2. escola pública que bloqueia o github no firewall existe, e é exatamente
 *     onde este produto roda;
 *  3. a página aponta para o próprio domínio, sem intermediário.
 *
 * `false` enquanto o servidor não estiver preparado. Trocar esta linha é a
 * única mudança necessária — a página lê os dois endereços deste arquivo.
 *
 * E O ESPELHO CONTINUA CITADO em qualquer um dos dois casos: os dois caminhos
 * se cobrem. Escola que bloqueia o github usa o portal; problema no portal, o
 * leitor tem para onde ir sem esperar conserto.
 */
const SERVIDO_PELO_PORTAL = false;

export interface ReleaseDoHost {
  versao: string;
  /** ISO (AAAA-MM-DD). Nulo enquanto a release não foi aberta. */
  data: string | null;
  /** Tamanho do instalador em MB, arredondado. Nulo enquanto não se sabe. */
  tamanhoMb: number | null;
  /**
   * FALSO enquanto o repositório público não existe.
   *
   * Não é firula: com `false`, a página explica a modalidade inteira e não
   * oferece um botão que devolve 404. Publicar o botão antes do arquivo é o
   * achado 139 outra vez — a promessa que a página não pode cumprir —, e desta
   * vez com a página inteira construída em cima dela.
   */
  disponivel: boolean;
}

export const RELEASE_DO_HOST: ReleaseDoHost = {
  versao: '1.0.3',
  data: '2026-09-26',
  /* 76.839.680 bytes, lidos do `Content-Length` da URL estável em 26/09/2026, sem
   * autenticação nenhuma — que é como um professor de outra escola a baixaria. */
  tamanhoMb: 73,
  disponivel: true,
};

/**
 * A URL ESTÁVEL: `latest/download` resolve sozinho para a versão mais nova.
 * Um link com o número dentro envelheceria na página a cada release.
 */
export const URL_NO_GITHUB = `${REPO_PUBLICO}/releases/latest/download/Host-Sala-Offline-Setup.exe`;

/** No portal o nome também é estável, e o Nginx serve esse com `no-store` —
 *  senão o cache do Cloudflare entregaria a versão anterior para sempre. */
export const URL_NO_PORTAL = 'https://www.portalsimulados.com.br/downloads/Host-Sala-Offline-Setup.exe';

export const URL_DO_INSTALADOR = SERVIDO_PELO_PORTAL ? URL_NO_PORTAL : URL_NO_GITHUB;

/** O outro endereço, o que a página cita em letra menor. */
export const URL_ESPELHO = SERVIDO_PELO_PORTAL ? URL_NO_GITHUB : URL_NO_PORTAL;

/** Só há espelho a citar quando o portal já serve o arquivo. */
export const TEM_ESPELHO: boolean = SERVIDO_PELO_PORTAL;

export const URL_DAS_RELEASES = `${REPO_PUBLICO}/releases`;

/**
 * O aplicativo na App Store.
 *
 * Mora aqui, junto do instalador do host, porque a página `/como-usar` cita os
 * dois e a pergunta é a mesma: onde se obtém cada forma de acesso. Não há
 * equivalente do Android — o aplicativo dele não existe, e um link vazio seria
 * pior que a frase que explica.
 */
export const URL_APP_STORE = 'https://apps.apple.com/app/portal-simulados/id6761577308';

/** "1.0.1 · 4 de setembro de 2026 · 80 MB", omitindo o que ainda não se sabe. */
export function descricaoDoArquivo(r: ReleaseDoHost = RELEASE_DO_HOST): string {
  const partes = [`Versão ${r.versao}`];
  if (r.data) {
    partes.push(
      new Date(`${r.data}T12:00:00`).toLocaleDateString('pt-BR', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    );
  }
  if (r.tamanhoMb) partes.push(`${r.tamanhoMb} MB`);
  return partes.join(' · ');
}
